/**
 * The customer error dictionary — the SECOND layer of the OptiSigns emitter
 * contract, and the source of truth for the `/error-codes` reference page.
 *
 * Layer 1 is `reason`: a coarse, internally-renameable routing key that alert
 * rules filter on. It lives in each service's `<surface>-failure.log.ts` and is
 * NOT customer-facing. Layer 2 is `code`: a stable, permanent, documented string
 * with copy a customer can act on.
 *
 * The two cannot be one field. The emitter contract forbids free text in a log
 * line (no `error.message`, no stack), so a line carries no explanation BY
 * CONSTRUCTION — the copy has to live outside it. This is that outside.
 *
 * EVERY REASON GETS A CODE. What varies is the COPY, not the existence of the
 * code. Where the honest cause is ours, our infrastructure, or unattributable,
 * the message is deliberately generic — but the code still exists, so a customer
 * has something stable to quote and support has something to search on.
 *
 * This follows Stripe: `fraudulent`, `lost_card` and `stolen_card` all have
 * codes, and each carries "present it in the same manner as generic_decline".
 * The code is the contract; the message is the disclosure decision. Withholding
 * the code as well leaves a customer with nothing to reference and forces every
 * such case into a free-text support email.
 *
 * ⚠️ Read the `note` before rendering one. Some carry hard renderer rules — most
 * importantly route_not_found, whose `path` field echoes attacker-controlled
 * input from OTHER callers and must never be shown.
 *
 * Transcribed per lane from the owning repo's contract module, with the source
 * path recorded on the lane. It is a TRANSCRIPTION, not an import — the lanes
 * live in other repos (optisigns-mdm-api, optisigns-rest-api, …), so this file
 * WILL drift if a union changes and nobody updates it. `reason` values are
 * asserted against the source module in the lane comment; re-check on edit.
 *
 * Contract + method: os-claude-harness
 * `.claude/skills/structured-logging/references/customer-dictionary.md`.
 */

/** How loud the emitter is, NOT how severe the incident is. */
export type ErrorLevel = 'error' | 'warn' | 'info';

/**
 * The seven taxonomy classes. Which class a reason lands in already decides
 * whether it can be shown to a customer — see `visibility`.
 */
export type ErrorTaxonomyClass =
  /** State is wrong and stays wrong; a human must reconcile. Never customer-facing. */
  | 'correctness_broken'
  /** The user cannot proceed, but nothing is corrupted. */
  | 'user_blocked'
  /** OUR credential/config is wrong, so it is broken for EVERYONE. Never customer-facing. */
  | 'config_regression'
  /** Expected, and actionable by the other party. The dictionary's main population. */
  | 'caller_actionable'
  /** The upstream had a bad moment. Aggregate is the signal. */
  | 'provider_transient'
  /** The main operation succeeded; a non-blocking follow-up did not. */
  | 'side_effect'
  /** The anti-silence net. Required in every union. */
  | 'unclassified';

export interface ErrorReasonEntry {
  /** Layer 1 — the value that appears in the log line's `reason` field. */
  reason: string;
  level: ErrorLevel;
  taxonomy: ErrorTaxonomyClass;
  /**
   * Layer 2 — the stable customer-facing code. EVERY reason gets one. Flat
   * readable snake_case, never a numeric range: a range cannot be re-partitioned
   * once customers key on it.
   */
  code: string;
  /**
   * The copy. Must say WHAT TO DO — "invalid_grant helps nobody" (the rule the
   * Power BI port encoded after shipping the opposite).
   *
   * Where the honest cause is ours, our infrastructure, or unattributable, the
   * message is deliberately GENERIC — the code still exists, the explanation is
   * withheld. This is Stripe's documented pattern: `fraudulent`, `lost_card` and
   * `stolen_card` all have codes, and all carry "present it in the same manner as
   * generic_decline". Withholding the code as well was stricter than the
   * precedent and left the customer with nothing to quote in a ticket.
   */
  customerMessage: string;
  /**
   * The human-readable page, for a `doc_url` on the emitted event (the Stripe
   * pattern: every instance carries a link to its own explanation).
   * `null` = this code has no article yet. That is a REAL gap, rendered as one.
   */
  docUrl?: string | null;
  /**
   * Why this copy, and anything a renderer must not do with this reason.
   * Internal — never shown to a customer.
   */
  note: string;
}

export interface ErrorLane {
  /** Short lane key, e.g. 'mdm'. */
  key: string;
  title: string;
  /** Repo that owns the contract module. */
  repo: string;
  /** Path to the module inside that repo — the thing this was transcribed from. */
  module: string;
  /** The stable token every rule for this lane greps for. */
  evt: string;
  /**
   * The PUBLIC product-area namespace for provenance. Deliberately not a repo,
   * container or pod name: nobody in the industry publishes internal service
   * names, and ours are not 1:1 with repos anyway (one repo ships three
   * containers). Operators get provenance from the `container` stream label.
   */
  area: string;
  /** One-paragraph orientation for someone reading the lane cold. */
  summary: string;
  reasons: ErrorReasonEntry[];
}

/**
 * MDM — device management (optisigns-mdm-api).
 *
 * Transcribed from `src/common/mdm-failure.log.ts` (MdmFailureReason,
 * LEVEL_BY_REASON) as of 2026-08-27. 12 reasons, and exactly TWO of them can be
 * shown to a customer. That ratio is the point of this page, not a defect in the
 * lane: device-management failures are overwhelmingly our own to fix.
 */
const MDM_LANE: ErrorLane = {
  key: 'mdm',
  title: 'Device management (MDM)',
  repo: 'optisigns-mdm-api',
  module: 'src/common/mdm-failure.log.ts',
  evt: 'mdm_fail',
  area: 'devices.management',
  summary:
    'The MDM control plane — device check-in, command dispatch, SecureLink provisioning. ' +
    'Highest-volume surface in the estate. All 12 reasons carry a code; 10 of them describe ' +
    'something the customer cannot fix, so their copy is deliberately generic.',
  reasons: [
    {
      reason: 'device_state_write_failed',
      level: 'error',
      taxonomy: 'correctness_broken',
      code: 'screen_state_not_saved',
      customerMessage:
        "We couldn't save this screen's latest state. Your content and schedule are unaffected. If the screen keeps showing information that looks out of date, contact support with the screen name.",
      docUrl: null,
      note: 'Correctness-broken: state may be wrong until a human reconciles it. Copy is deliberately generic — the customer cannot fix it, so it sets expectations and routes them to support rather than describing our data layer.',
    },
    {
      reason: 'mdm_config_regression',
      level: 'error',
      taxonomy: 'config_regression',
      code: 'service_temporarily_unavailable',
      // SHARED COPY — must stay byte-identical to rest_config_regression below.
      // See that entry's note for why, and change both together or neither.
      customerMessage:
        "We couldn't complete this request. It's a problem on our side and our team is already alerted — no action needed. Your screens keep playing their current content.",
      docUrl: null,
      note: 'OUR credential/config is broken, so it is broken for EVERY device; pages on first occurrence. GENERIC BY DESIGN — never name the credential, the provider or the config. The reassurance that playback continues is the useful part. The copy used to open "Screen management is temporarily unavailable", which was accurate here and FALSE on the player REST surface that issues the same code — a shared code cannot name one service. It also must not say "try again": unlike temporary_service_issue, this is broken for everyone until WE deploy a fix, so retrying is useless advice.',
    },
    {
      reason: 'unclassified',
      level: 'error',
      taxonomy: 'unclassified',
      code: 'unexpected_error',
      customerMessage:
        "Something went wrong that we don't have a specific explanation for yet. We've recorded it. If it happens again, contact support and tell us roughly when — that lets us find this exact event.",
      docUrl: null,
      note: 'The anti-silence net; also absorbs programmer errors, so it is disproportionately our own bugs. Asking for the time is deliberate: it is what makes the event findable, and it is the only thing the customer can usefully supply.',
    },
    {
      reason: 'auth_signature_expired',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'device_clock_out_of_sync',
      customerMessage:
        "This screen's clock has drifted too far from real time, so it can no longer authenticate. Restart the screen; if it keeps happening, set the device to sync time automatically (NTP) in its network settings.",
      docUrl: null,
      note: 'Deliberately separate from auth_signature_invalid: the signing window is already widened for unreliable device clocks, so an expiry past even that is far more likely a drifted device than an attack. Fully specific copy — the customer can genuinely fix this.',
    },
    {
      reason: 'securelink_provision_failed',
      level: 'warn',
      taxonomy: 'user_blocked',
      code: 'remote_session_unavailable',
      customerMessage:
        'We could not open a remote troubleshooting session with this screen. Check that it is online and try again in a few minutes.',
      docUrl: null,
      note: 'The customer STARTED this and watches it fail, so silence is worse than a vague answer. Copy names no infrastructure — never mention VPN, WireGuard or the orchestrator.',
    },
    {
      reason: 'auth_key_unknown',
      level: 'warn',
      taxonomy: 'user_blocked',
      code: 'device_not_recognised',
      customerMessage:
        "This screen's credentials are not recognised. Re-pair the screen from Screens → Add Screen; if it was recently replaced or factory reset, re-pairing is expected.",
      docUrl: null,
      note: 'Concerns our shared signing keys, not the customer credentials — so the copy gives the remedy (re-pair) without describing what was checked. Do NOT render accessKeyId to a customer.',
    },
    {
      reason: 'auth_signature_invalid',
      level: 'warn',
      taxonomy: 'user_blocked',
      code: 'device_credential_rejected',
      customerMessage:
        "We could not verify this screen's identity. Re-pair the screen; if that does not resolve it, contact support.",
      docUrl: null,
      note: 'A genuine signature mismatch, treated as possibly hostile. Copy is deliberately thin: never explain WHAT failed verification, or the message becomes a probing oracle.',
    },
    {
      reason: 'auth_key_missing',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'device_credential_missing',
      customerMessage:
        'This screen did not send its credentials. Restart it; if it still cannot connect, re-pair it from the portal.',
      docUrl: null,
      note: 'Overwhelmingly scanners and misconfigured integrations rather than a real customer screen. Safe copy, but see the attribution warning on route_not_found — the same caveat applies.',
    },
    {
      reason: 'orchestrator_transient',
      level: 'warn',
      taxonomy: 'provider_transient',
      code: 'temporary_service_issue',
      customerMessage:
        'A temporary issue on our side interrupted this. It usually clears on its own — try again in a few minutes.',
      docUrl: null,
      note: 'GENERIC BY DESIGN. The honest cause names our own datastores and network layer. The aggregate across many accounts is the real signal, and that is a dashboard, not a customer message.',
    },
    {
      reason: 'command_dispatch_timeout',
      level: 'warn',
      taxonomy: 'provider_transient',
      code: 'command_not_delivered',
      customerMessage:
        'We could not deliver that command to the screen in time. Check the screen is online and try again.',
      docUrl: null,
      note: 'The customer issued the command and it visibly did nothing, so this needs an answer. Copy describes the OUTCOME (not delivered), never the transport.',
    },
    {
      reason: 'pubsub_notify_failed',
      level: 'warn',
      taxonomy: 'side_effect',
      code: 'followup_notification_failed',
      customerMessage:
        'Your change was applied, but a follow-up notification did not send. No action needed — the screen already has the update.',
      docUrl: null,
      note: 'The main operation SUCCEEDED. The copy must lead with that, or it reports a failure the customer did not experience and generates a support contact for a non-problem.',
    },
    {
      reason: 'presence_check_timeout',
      level: 'warn',
      taxonomy: 'provider_transient',
      code: 'screen_status_may_be_stale',
      customerMessage:
        "This screen's online/offline status may be out of date for a few minutes. It corrects itself the next time the screen connects — the screen itself is unaffected and keeps playing.",
      docUrl: null,
      note: 'Added by eb23e88 (2026-08-19) and MISSED by the first transcription of this union — the exact drift this file warns about. Blast radius is a stale utilsOnline flag for ONE device until its next connect stamps it, which is why the lane rates it warn and not a state-correctness reason. Copy names what the customer sees, never the redis fan-in behind it.',
    },
    {
      reason: 'route_not_found',
      level: 'info',
      taxonomy: 'caller_actionable',
      code: 'unsupported_request',
      customerMessage:
        'This screen asked for something this version of OptiSigns does not support. Update the OptiSigns app on the screen to the latest version.',
      docUrl: null,
      note: '⚠️ ~100% of this service\'s measured volume (9,360/7d) and mostly scanners, not customer screens. TWO renderer rules: never surface it unless the event is attributable to the asking account, and NEVER render the `path` field — it echoes attacker-controlled input from other callers.',
    },
  ],
};


/**
 * Which query dialect a lane's lines need. Not cosmetic — a rule written for one
 * returns ZERO against the other, silently.
 */
export type LaneTransport = 'backend' | 'faro';

/**
 * EVERY known emitter lane in the estate, whether or not it has a dictionary yet.
 *
 * The unit is the SURFACE, not the repo: open-api owns two lanes and scio3 owns
 * three, because SAML failures and Firebase-token failures are different
 * taxonomies with different alert rules. Forcing them under one `evt` would mean
 * one union covering both.
 *
 * `evt` is the grep token every rule prefilters on (`|= "mdm_fail"`), before the
 * expensive parse. It is never customer-facing.
 *
 * ⚠️ The naming is NOT consistent: six lanes are `*_fail`, three are `*_error`.
 * A rule author who assumes `_fail` writes a query that silently matches nothing
 * against the Faro lanes. Renaming an `evt` breaks every ruler rule matching it,
 * so standardising is a coordinated change with logs-stack-v2, not a tidy-up.
 */
export interface LaneRef {
  evt: string;
  repo: string;
  lane: string;
  module: string;
  transport: LaneTransport;
  /** Notes worth knowing before writing a rule or a dictionary entry for it. */
  note?: string;
}

export const LANE_REGISTRY: LaneRef[] = [
  {
    evt: 'mdm_fail',
    repo: 'optisigns-mdm-api',
    lane: 'Device management',
    module: 'src/common/mdm-failure.log.ts',
    transport: 'backend',
    note: 'Emits `code` beside `reason`. Its typed CODE_BY_REASON is the shape every other lane is being brought to.',
  },
  {
    evt: 'rest_fail',
    repo: 'optisigns-rest-api',
    lane: 'Player REST',
    module: 'app/rest-failure.log.js',
    transport: 'backend',
    note: 'Highest-volume surface in the estate. 27 emit sites; 23 carry no identifier at all, so most lines cannot be attributed to an account.',
  },
  {
    evt: 'sharepoint_fail',
    repo: 'smallapps/optisigns-smallapp-sharepoint-news',
    lane: 'Integration sync',
    module: 'apps/rest/src/core/sharepoint-failure.log.ts',
    transport: 'backend',
    note: 'In the dictionary since 2026-09-04, with a typed CODE_BY_REASON the drift checker verifies. Its classifier descends from Power BI lib/sp-error.js. Only TWO call sites emit today, so 3 of its 9 reasons are declared but not yet reachable — see the lane summary.',
  },
  {
    evt: 'auth_verify_fail',
    repo: 'optisigns-open-api',
    lane: 'Firebase token verify',
    module: 'src/app/auth/jwt.strategy.ts',
    transport: 'backend',
    note: 'Carries no tenant id, so the main login-failure path cannot be scoped to a customer — unlike the SAML lane beside it.',
  },
  {
    evt: 'saml_auth_fail',
    repo: 'optisigns-open-api',
    lane: 'SSO / SAML',
    module: 'src/app/filters/saml-auth.filter.ts',
    transport: 'backend',
    note: 'Carries accountId.',
  },
  {
    evt: 'upload_fail',
    repo: 'optisigns-open-api',
    lane: 'File uploads',
    module: 'src/app/common/upload-failure.log.ts',
    transport: 'backend',
    note: 'Covers upload-option gates, storage copies and share-link upload authorisation. The source lane still needs a typed CODE_BY_REASON map for automated drift checks.',
  },
  {
    evt: 'auth_error',
    repo: 'scio3',
    lane: 'Browser auth',
    module: 'apps/web/src/lib/telemetry/faro.ts',
    transport: 'faro',
  },
  {
    evt: 'payment_error',
    repo: 'scio3',
    lane: 'Browser payment',
    module: 'apps/web/src/lib/telemetry/payment-telemetry.ts',
    transport: 'faro',
    note: 'The evt does not match the `payment_signals` name its rules use — check the rule, not the token, when a query returns nothing.',
  },
  {
    evt: 'pair_device_fail',
    repo: 'scio3',
    lane: 'Browser pairing',
    module: 'apps/web/src/lib/telemetry/faro.ts',
    transport: 'faro',
    note: 'Uses `host` as a tenant proxy — real signal for white-label customers on their own domain, constant and useless for everyone on app.optisigns.com.',
  },
  {
    evt: 'upload_error',
    repo: 'scio3',
    lane: 'Browser file uploads',
    module: 'apps/web/src/lib/telemetry/faro.ts',
    transport: 'faro',
    note: 'Browser-side upload options, assembly, save and thumbnail failures. Uses Faro kind/code rather than the backend reason dictionary.',
  },
];

/** Lanes with a dictionary, by evt. */
export function lanesWithCodes(): Set<string> {
  return new Set(ERROR_LANES.map((l) => l.evt));
}


/**
 * Player REST — optisigns-rest-api.
 *
 * Transcribed from `app/rest-failure.log.js` (REST_FAILURE_REASONS, CODE_BY_REASON)
 * as of 2026-08-27. 13 reasons, 11 distinct codes: three of them collapse to
 * `unexpected_error`, which is the reason/code split doing its job — our
 * compound-id parser, our malformed-websocket handler and the anti-silence net are
 * three alerting decisions and one customer experience.
 *
 * ⚠️ ATTRIBUTION IS THE BLOCKER HERE, not the codes. 23 of this lane's 27 emit
 * sites carry no identifier at all, and `accountId` is passed at NONE of them, so
 * most lines cannot be tied to the customer asking. Codes are necessary but not
 * sufficient: do not surface any of these to a customer until the emitting site
 * carries a tenant id.
 */
const REST_LANE: ErrorLane = {
  key: 'rest',
  title: 'Player REST',
  repo: 'optisigns-rest-api',
  module: 'app/rest-failure.log.js',
  evt: 'rest_fail',
  area: 'devices.player',
  summary:
    'The surface every player talks to — device check-in, content pull, pairing. Highest-volume ' +
    'service in the estate. Codes exist for all 13 reasons, but most emit sites carry no tenant ' +
    'id, so attribution, not vocabulary, is what gates showing these to a customer.',
  reasons: [
    {
      reason: 'device_state_write_failed',
      level: 'error',
      taxonomy: 'correctness_broken',
      code: 'screen_state_not_saved',
      customerMessage:
        "We couldn't save this screen's latest state. Your content and schedule are unaffected. If the screen keeps showing information that looks out of date, contact support with the screen name.",
      docUrl: null,
      note: 'Same code as the mdm lane deliberately — identical customer experience, two different services. Correctness-broken: may need a human to reconcile.',
    },
    {
      reason: 'device_unpair_failed',
      level: 'error',
      taxonomy: 'correctness_broken',
      code: 'screen_unpair_incomplete',
      customerMessage:
        'This screen may not have fully unpaired. If it still appears in your account or keeps playing content, contact support with the screen name.',
      docUrl: null,
      note: 'State may be inconsistent in a way the customer can SEE (a ghost screen), so the copy names the symptom they would notice rather than the write that failed.',
    },
    {
      reason: 'rest_config_regression',
      level: 'error',
      taxonomy: 'config_regression',
      code: 'service_temporarily_unavailable',
      // SHARED COPY — must stay byte-identical to mdm_config_regression above.
      customerMessage:
        "We couldn't complete this request. It's a problem on our side and our team is already alerted — no action needed. Your screens keep playing their current content.",
      docUrl: null,
      note: 'GENERIC BY DESIGN — never name the credential, provider or config. Pages on first occurrence; baseline is 0. This code is issued by BOTH this lane and mdm_config_regression, and a code is what a customer quotes into a ticket — so the two must carry the SAME sentence or one string means two things. The wording is deliberately service-agnostic for that reason: it has to be true whether the failure was in screen management or in the player API.',
    },
    {
      reason: 'unclassified',
      level: 'error',
      taxonomy: 'unclassified',
      code: 'unexpected_error',
      customerMessage:
        "Something went wrong that we don't have a specific explanation for yet. We've recorded it. If it happens again, contact support and tell us roughly when — that lets us find this exact event.",
      docUrl: null,
      note: 'Also absorbs programmer errors (TypeError etc. short-circuit here), so it is disproportionately our own bugs. Asking for the time is the one useful thing a customer can supply.',
    },
    {
      reason: 'compound_id_parse_failed',
      level: 'error',
      taxonomy: 'unclassified',
      code: 'content_reference_invalid',
      customerMessage:
        "Something went wrong that we don't have a specific explanation for yet. We've recorded it. If it happens again, contact support and tell us roughly when — that lets us find this exact event.",
      docUrl: null,
      note: 'OUR parser bug — historically the single most common error-level line in this service, and it was mis-filed as the caller fault before the programmer-error short-circuit landed. SHARES THE PRESENTATION of unexpected_error, never the code: the customer can do nothing differently, but a distinct code is what lets support correlate a report to this specific bug without opening the logs. That is the Stripe pattern (lost_card / stolen_card / fraudulent each keep a decline_code and each says "present in the same manner as generic_decline").',
    },
    {
      reason: 'ws_message_malformed',
      level: 'error',
      taxonomy: 'unclassified',
      code: 'device_message_invalid',
      customerMessage:
        "Something went wrong that we don't have a specific explanation for yet. We've recorded it. If it happens again, contact support and tell us roughly when — that lets us find this exact event.",
      docUrl: null,
      note: 'A websocket frame we could not parse: our protocol bug, not the player\'s. Emitted with no device id in scope — only argCount/expectLen — so it is uninterpretable per-customer, which is a real gap in the emitter rather than a reason to withhold the code. Shares the presentation of unexpected_error, not the code.',
    },
    {
      reason: 'auth_signature_expired',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'device_clock_out_of_sync',
      customerMessage:
        "This screen's clock has drifted too far from real time, so it can no longer authenticate. Restart the screen; if it keeps happening, set the device to sync time automatically (NTP) in its network settings.",
      docUrl: null,
      note: '⚠️ SAMPLED 1-in-100 in this lane (295,835 403s/day against ~636 log lines/day total). Absence of lines is NOT absence of the problem, and any count must multiply by `sampled`. Same code as mdm — same customer fix.',
    },
    {
      reason: 'auth_key_unknown',
      level: 'warn',
      taxonomy: 'user_blocked',
      code: 'device_not_recognised',
      customerMessage:
        "This screen's credentials are not recognised. Re-pair the screen from Screens → Add Screen; if it was recently replaced or factory reset, re-pairing is expected.",
      docUrl: null,
      note: 'Concerns our shared signing keys. Give the remedy, never describe what was checked. Do NOT render accessKeyId.',
    },
    {
      reason: 'auth_signature_invalid',
      level: 'warn',
      taxonomy: 'user_blocked',
      code: 'device_credential_rejected',
      customerMessage:
        "We could not verify this screen's identity. Re-pair the screen; if that does not resolve it, contact support.",
      docUrl: null,
      note: 'A genuine mismatch, treated as possibly hostile. Deliberately thin: explaining WHAT failed turns the message into a probing oracle.',
    },
    {
      reason: 'auth_key_missing',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'device_credential_missing',
      customerMessage:
        'This screen did not send its credentials. Restart it; if it still cannot connect, re-pair it from the portal.',
      docUrl: null,
      note: 'Largely scanners and misconfigured integrations rather than real customer screens — attribution matters more than the copy here.',
    },
    {
      reason: 'content_resolve_failed',
      level: 'error',
      taxonomy: 'user_blocked',
      code: 'content_unavailable',
      customerMessage:
        "We couldn't work out what this screen should be playing. Check the screen still has a playlist or schedule assigned; if it does, contact support with the screen name.",
      docUrl: null,
      note: 'The closest thing in this lane to "why is my screen blank". Named for what the screen SHOWS, not for the playlist/schedule/companyData resolution that failed. Fires across several resolution paths with no tenant id attached today.',
    },
    {
      reason: 'upstream_transient',
      level: 'warn',
      taxonomy: 'provider_transient',
      code: 'temporary_service_issue',
      customerMessage:
        'A temporary issue on our side interrupted this. It usually clears on its own — try again in a few minutes.',
      docUrl: null,
      note: 'GENERIC BY DESIGN. The honest cause names our datastores. The aggregate across many accounts is the signal, and that is a dashboard, not a customer message.',
    },
    {
      reason: 'side_effect_failed',
      level: 'warn',
      taxonomy: 'side_effect',
      code: 'followup_action_failed',
      customerMessage:
        'Your request was completed, but a follow-up step did not finish. No action needed — if anything looks wrong on the screen, contact support.',
      docUrl: null,
      note: 'The main operation SUCCEEDED; lead with that. Reporting it as a failure manufactures a support contact for a non-problem.',
    },
  ],
};

/**
 * File uploads (optisigns-open-api).
 *
 * Transcribed from `src/app/common/upload-failure.log.ts` as of 2026-09-03.
 * The source lane currently has no typed CODE_BY_REASON map, so the harness drift
 * checker cannot verify this transcription until that map is added in Open API.
 */
const UPLOAD_LANE: ErrorLane = {
  key: 'upload',
  title: 'File uploads',
  repo: 'optisigns-open-api',
  module: 'src/app/common/upload-failure.log.ts',
  evt: 'upload_fail',
  area: 'content.uploads',
  summary:
    'Failures while requesting upload options, transferring files, copying stored files, ' +
    'or authorising uploads through a share link. Customer-actionable rejections have ' +
    'specific guidance; service and configuration failures use deliberately generic copy.',
  reasons: [
    {
      reason: 'storage_disabled',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'file_storage_not_enabled',
      customerMessage:
        'File uploads are not enabled for this account. Ask an account administrator to enable file storage, then try again.',
      docUrl: null,
      note: 'Both configured storage providers are disabled for the account. Describe the account setting, never the provider names or storage configuration.',
    },
    {
      reason: 'api_upload_not_enabled',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'api_file_upload_not_enabled',
      customerMessage:
        'Uploading files through the API is not enabled for this account. Ask an account administrator to review the plan or API upload settings.',
      docUrl: null,
      note: 'Expected plan or feature-gate rejection. The customer can resolve it through account configuration; do not describe internal plan-feature evaluation.',
    },
    {
      reason: 'share_link_denied',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'share_link_upload_not_allowed',
      customerMessage:
        'This share link does not allow file uploads. Request an editor link from the content owner and try again.',
      docUrl: null,
      note: 'Covers an invalid share-link session and a role without upload permission. Keep one response for both cases so the public endpoint does not disclose access details.',
    },
    {
      reason: 'account_missing',
      level: 'error',
      taxonomy: 'user_blocked',
      code: 'file_upload_unavailable',
      customerMessage:
        "We couldn't start this upload. Refresh the page and try again; if it continues, contact support and mention when it happened.",
      docUrl: null,
      note: 'The authenticated account could not be loaded. Generic by design: do not disclose account lookup or persistence details.',
    },
    {
      reason: 'storage_upload_failed',
      level: 'error',
      taxonomy: 'user_blocked',
      code: 'file_upload_failed',
      customerMessage:
        "We couldn't finish uploading this file. Try again; if it continues, contact support and mention when it happened.",
      docUrl: null,
      note: 'The storage copy failed and the user remains blocked. Do not expose provider names, object keys, file names or the upstream error.',
    },
    {
      reason: 'transforge_config_incomplete',
      level: 'error',
      taxonomy: 'config_regression',
      code: 'file_upload_service_misconfigured',
      customerMessage:
        "We couldn't complete this upload because of a problem on our side. No account changes are needed; try again later.",
      docUrl: null,
      note: 'Internal deployment/configuration defect. Never expose the provider name, credential, endpoint, template or fallback behavior.',
    },
    {
      reason: 'unclassified',
      level: 'error',
      taxonomy: 'unclassified',
      code: 'unexpected_upload_error',
      customerMessage:
        'Something unexpected interrupted this upload. Try again; if it continues, contact support and mention when it happened.',
      docUrl: null,
      note: 'Anti-silence fallback, including programmer errors. Generic copy is required because the cause is unknown.',
    },
  ],
};

/**
 * SharePoint News (smallapps/optisigns-smallapp-sharepoint-news).
 *
 * Transcribed from `apps/rest/src/core/sharepoint-failure.log.ts` as of 2026-09-04.
 * 9 reasons, 9 codes, verified in sync by the harness drift checker.
 *
 * The first lane in the dictionary where naming the failing SERVICE is correct
 * rather than a leak. On the device lanes a shared code like
 * `service_temporarily_unavailable` cannot name one service, because several
 * surfaces issue it. Here the integration IS the feature the customer bought and
 * turned on, so "SharePoint" is the product name, not internal architecture. What
 * still must never appear is the credential, the tenant, the site path or OUR app
 * registration.
 *
 * This lane also CLASSIFIES: the reason on the line is the classifier's verdict,
 * not the call site's guess, so a `customer_site_unavailable` site routinely emits
 * `customer_credential_invalid` when the error carries an AADSTS code.
 */
const SHAREPOINT_LANE: ErrorLane = {
  key: 'sharepoint',
  title: 'SharePoint News',
  repo: 'smallapps/optisigns-smallapp-sharepoint-news',
  module: 'apps/rest/src/core/sharepoint-failure.log.ts',
  evt: 'sharepoint_fail',
  area: 'integrations.sharepoint',
  summary:
    'The SharePoint News integration — Microsoft sign-in, site and list access, asset ' +
    'sync and app configuration. Unusually customer-actionable for this estate: four of ' +
    'the nine reasons describe something an account administrator can genuinely fix, ' +
    'because the customer supplies the credentials and the site.',
  reasons: [
    {
      reason: 'sharepoint_config_regression',
      level: 'error',
      taxonomy: 'config_regression',
      code: 'sharepoint_signin_unavailable',
      customerMessage:
        "SharePoint News can't connect to Microsoft right now because of a problem on our side. Our team is already alerted — no action needed, and your saved connection settings are fine. Screens keep showing the posts they last received.",
      docUrl: null,
      note: "OUR app registration is broken, so direct-login sign-in fails for EVERY customer; pages on first occurrence. Naming SharePoint is correct here — it is the feature the customer enabled — but never name the registration, the client id or the AADSTS code. \"Your saved connection settings are fine\" is the load-bearing sentence: without it the obvious customer response is to delete and re-enter a working configuration.",
    },
    {
      reason: 'integration_key_undecryptable',
      level: 'error',
      taxonomy: 'config_regression',
      code: 'sharepoint_config_unreadable',
      customerMessage:
        "We couldn't read the SharePoint connection saved for this account. It's a problem on our side and our team is already alerted. Please don't re-enter or delete your credentials — they aren't the cause, and replacing them won't fix it.",
      docUrl: null,
      note: 'DELIBERATELY a separate code from sharepoint_signin_unavailable despite the identical blast radius. Named for the stored CONFIG rather than the credential on purpose: a code reading \'credentials\' invites the exact re-entry the copy tells them not to do. Our INTEGRATION_KEY_SECRET cannot decrypt a stored credential; the credential itself is intact. The remedies are opposite, and the natural customer instinct — re-enter the credentials — destroys a good record for nothing. One code for both classes would lose exactly that sentence. Never mention encryption, key rotation or the secret store.',
    },
    {
      reason: 'customer_credential_invalid',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'sharepoint_signin_rejected',
      customerMessage:
        'Microsoft rejected the credentials saved for this SharePoint connection. An expired client secret is the most common cause. Check the client ID, client secret and tenant ID in the app settings, confirm an administrator has granted consent, then reconnect.',
      docUrl: null,
      note: "The customer's own Service Principal is wrong, expired, or unconsented — genuinely theirs to fix, and the most common real failure in this lane. Naming the three fields and the consent step is the whole value of the code; it is what turns a support ticket into a self-service fix. Do NOT echo the AADSTS code into the copy: it is on the log line for us, and it means nothing to the person reading this.",
    },
    {
      reason: 'customer_site_unavailable',
      level: 'warn',
      taxonomy: 'caller_actionable',
      code: 'sharepoint_site_not_accessible',
      customerMessage:
        "We couldn't open the SharePoint site for this app. Check that the site URL is correct and that the connected account has been granted access to it, then try again.",
      docUrl: null,
      note: 'The site URL is wrong, or this identity was never granted access. Deliberately ONE response for both: a 403 and a 404 answered differently would let anyone probe which SharePoint sites exist in a tenant. Never echo the site path back — the caller supplied it, and reflecting caller input is how a log line becomes an injection surface.',
    },
    {
      reason: 'provider_transient',
      level: 'warn',
      taxonomy: 'provider_transient',
      code: 'temporary_service_issue',
      // SHARED COPY — byte-identical to mdm orchestrator_transient and rest
      // upstream_transient. Change all three together or none.
      customerMessage:
        'A temporary issue on our side interrupted this. It usually clears on its own — try again in a few minutes.',
      docUrl: null,
      note: 'Microsoft throttled us or had a 5xx, or the network reset. GENERIC BY DESIGN and shared across lanes: the customer action is identical whoever the upstream was, and the aggregate is a dashboard, not a message. Note the copy says "on our side" even though the provider is Microsoft — we chose the dependency, so it is ours to own.',
    },
    {
      reason: 'sync_job_not_scheduled',
      level: 'warn',
      taxonomy: 'side_effect',
      code: 'followup_action_failed',
      // SHARED COPY — byte-identical to rest side_effect_failed.
      customerMessage:
        'Your request was completed, but a follow-up step did not finish. No action needed — if anything looks wrong on the screen, contact support.',
      docUrl: null,
      note: 'The save SUCCEEDED and the follow-up sync was not queued; posts refresh late rather than not at all. Lead with the success — reporting this as a failure manufactures a support contact for a non-problem.',
    },
    {
      reason: 'asset_operation_failed',
      level: 'error',
      taxonomy: 'user_blocked',
      code: 'content_update_failed',
      customerMessage:
        "We couldn't finish updating this SharePoint News app. Nothing was changed, so it's safe to try again. If it keeps happening, contact support and mention roughly when.",
      docUrl: null,
      note: 'An asset CRUD call failed. State is CONSISTENT — that is why the copy can promise "nothing was changed" and invite a retry, which it must not do for any reason where a partial write is possible. Outcome-shaped: say what the customer could not do, never which call failed.',
    },
    {
      reason: 'config_save_failed',
      level: 'error',
      taxonomy: 'user_blocked',
      code: 'sharepoint_settings_not_saved',
      customerMessage:
        "We couldn't save these SharePoint News settings. Your previous settings are still in effect. Try again, and if it keeps happening, contact support and mention roughly when.",
      docUrl: null,
      note: 'Persisting the app config failed. Telling the customer the previous settings still apply is the useful half: it says what their screens are doing right now, which is the actual question behind the report.',
    },
    {
      reason: 'unclassified',
      level: 'error',
      taxonomy: 'unclassified',
      code: 'unexpected_error',
      // SHARED COPY — byte-identical to mdm and rest unclassified.
      customerMessage:
        "Something went wrong that we don't have a specific explanation for yet. We've recorded it. If it happens again, contact support and tell us roughly when — that lets us find this exact event.",
      docUrl: null,
      note: 'The anti-silence net. TypeError/ReferenceError/RangeError/SyntaxError short-circuit here before any other rule, so this reason is disproportionately OUR bugs rather than anything about SharePoint. Asking for the time is deliberate: it is what makes the event findable, and the only thing the customer can usefully supply.',
    },
  ],
};

/** Every lane in the dictionary. Add a lane here; the page renders it. */
export const ERROR_LANES: ErrorLane[] = [
  MDM_LANE,
  REST_LANE,
  UPLOAD_LANE,
  SHAREPOINT_LANE,
];

/** Every issued code, flattened with its lane. Every reason has one. */
export function customerCodes(lanes: ErrorLane[] = ERROR_LANES) {
  return lanes.flatMap((lane) => lane.reasons.map((r) => ({ lane, entry: r })));
}

/**
 * Codes with no article yet. The dictionary auditing itself: the Power BI port
 * split `terminal` from `terminal-unclassified` precisely so a missing entry was
 * a signal rather than a silence. A code a customer can be shown but cannot look
 * up is the Particle anti-pattern — visible and useless.
 */
export function codesMissingArticle(lanes: ErrorLane[] = ERROR_LANES) {
  return customerCodes(lanes).filter(({ entry }) => !entry.docUrl);
}
